/**
 * AddressAutocomplete — Google Places address autocomplete component
 *
 * Provides address suggestions as user types, then auto-fills
 * address fields when a suggestion is selected.
 *
 * Uses backend proxy (/api/address/*) to keep API key secure.
 * Ported from resume-optimizer, adapted to use billingApi.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField, Paper, List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, InputAdornment, ClickAwayListener, Box, Typography, Chip
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import billingApi from '../services/billingApi';

const DEBOUNCE_DELAY = 300;
const MIN_CHARS = 3;

/**
 * @param {Object} props
 * @param {string}   props.value        - Current street address value
 * @param {Function} props.onChange      - Called with street address string on input change
 * @param {Function} props.onAddressSelect - Called with full address object when user picks a suggestion
 * @param {string}   props.label
 * @param {string}   props.placeholder
 * @param {boolean}  props.error
 * @param {string}   props.helperText
 * @param {boolean}  props.required
 * @param {Object}   props.sx
 */
export default function AddressAutocomplete({
  value = '',
  onChange,
  onAddressSelect,
  label = 'Address',
  placeholder = 'Start typing your address...',
  error = false,
  helperText,
  required = false,
  sx = {}
}) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [verified, setVerified] = useState(false);
  const [sessionToken, setSessionToken] = useState(() =>
    `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );
  const debounceRef = useRef(null);

  useEffect(() => { setInputValue(value); }, [value]);

  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < MIN_CHARS) {
      setSuggestions([]);
      return;
    }
    try {
      setLoading(true);
      const params = new URLSearchParams({ input: text, sessionToken });
      const data = await billingApi.get(`/api/address/autocomplete?${params}`);
      setSuggestions(data.predictions || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setInputValue(val);
    setVerified(false);
    setShowSuggestions(true);
    if (onChange) onChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), DEBOUNCE_DELAY);
  }, [onChange, fetchSuggestions]);

  const handleSelect = useCallback(async (suggestion) => {
    try {
      setLoading(true);
      setShowSuggestions(false);

      const data = await billingApi.get(`/api/address/details/${suggestion.placeId}`);
      const addr = data.address;

      setInputValue(addr.street_address || suggestion.description);
      setVerified(true);

      if (onAddressSelect) {
        onAddressSelect({
          street_address: addr.street_address || '',
          city: addr.city || '',
          state: addr.state || '',
          zip_code: addr.zip_code || '',
          country: addr.country || 'US',
          full_address: addr.full_address || suggestion.description,
        });
      }

      // New session token for next search
      setSessionToken(`${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    } catch {
      setInputValue(suggestion.mainText || suggestion.description);
      if (onAddressSelect) {
        onAddressSelect({
          street_address: suggestion.mainText || suggestion.description,
          city: '', state: '', zip_code: '', full_address: suggestion.description,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [onAddressSelect]);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  return (
    <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
      <Box sx={{ position: 'relative', ...sx }}>
        <TextField
          fullWidth
          size="small"
          label={label}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          error={error}
          helperText={helperText || (verified ? undefined : inputValue.length >= MIN_CHARS ? 'Select a suggestion or type manually' : 'Start typing for address suggestions')}
          required={required}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : verified ? (
              <InputAdornment position="end">
                <Chip icon={<CheckCircleIcon />} label="Verified" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              </InputAdornment>
            ) : null,
          }}
          autoComplete="off"
        />

        {showSuggestions && suggestions.length > 0 && (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute', top: '100%', left: 0, right: 0,
              zIndex: 9999, maxHeight: 300, overflow: 'auto', mt: 0.5,
              border: '1px solid', borderColor: 'divider',
            }}
          >
            <List dense>
              {suggestions.map((s, i) => (
                <ListItem
                  key={s.placeId || i}
                  button
                  onClick={() => handleSelect(s)}
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LocationOnIcon color="action" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={s.mainText || s.description}
                    secondary={s.secondaryText}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ p: 0.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                Powered by Google
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}
